import { signal } from '@preact/signals';
import waitForTimers from 'wait-for-timers';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { idToEpisode } from '../global-state/podcasts';
import { removeFromListenLater, useUserDataStore } from '../global-state/user-data';
import { Episode } from '../models/podcasts';
import { useBumpEpisodePriorityWhenInView } from '../utils/bump-episode-priority-when-in-view';
import { checkIfPlayed } from '../utils/check-if-played';
import { classNames } from '../utils/helpers';
import { ButtonBar } from './button-bar';
import { EpisodeItem } from './episode-item';
import { Icon } from './icon';

const listenLaterFilter = signal<'all' | 'unplayed' | 'played'>('unplayed');
const playedFilterButtons = [
  {
    id: 'all',
    children: 'All',
  },
  {
    id: 'unplayed',
    children: 'Unplayed',
  },
  {
    id: 'played',
    children: 'Played',
  },
] as const;

export function ListenLaterTabContents() {
  // Use custom comparison function to re-render when an episode is marked as played
  // but not simply when its progress value changes
  const listenLaterEpisodesWithUnplayedFlag: [Episode, boolean][] =
    useStoreWithEqualityFn(
      useUserDataStore,
      (s) =>
        s.listenLaterEpisodeIds
          .map((id) => {
            const episode = idToEpisode(id);
            if (!episode) return;

            return [episode, checkIfPlayed(episode)];
          })
          .filter(Boolean),
      (oldArrays, newArrays) =>
        oldArrays &&
        oldArrays.length === newArrays.length &&
        oldArrays.every(([, isPlayed], index) => isPlayed === newArrays[index][1])
    );

  const filter = listenLaterFilter.value;
  const episodesToDisplay = listenLaterEpisodesWithUnplayedFlag
    .filter(([, isPlayed]) =>
      filter === 'all' ? true : filter === 'unplayed' ? !isPlayed : isPlayed
    )
    .map(([episode]) => episode);

  const bumpEpisodePriorityWhenInView = useBumpEpisodePriorityWhenInView();

  return (
    <div>
      {listenLaterEpisodesWithUnplayedFlag.length ? (
        <>
          <div class="mt-2 flex justify-center">
            <div class="flex rounded-lg border border-gray-200">
              <ButtonBar
                buttons={playedFilterButtons}
                selectedId={listenLaterFilter.value}
                onSelect={(tab) => {
                  listenLaterFilter.value = tab;
                }}
                buttonClassName={(id) =>
                  classNames(
                    'h-7 rounded-lg px-4',
                    id === 'all'
                      ? '[&_span]:rounded-r-none [&_span]:rounded-l-lg'
                      : id === 'played'
                        ? '[&_span]:rounded-l-none [&_span]:rounded-r-lg'
                        : ''
                  )
                }
              />
            </div>
          </div>
          {episodesToDisplay.map((episode) => (
            <EpisodeItem
              episode={episode}
              key={episode.id}
              swipeAction={{
                onSwipe: (animationDuration: number) => {
                  waitForTimers([animationDuration, 'ric', 'raf'], () =>
                    removeFromListenLater(episode)
                  );
                  return 'dismiss';
                },
                text: 'Remove',
                color: 'red',
              }}
              createContentRef={bumpEpisodePriorityWhenInView}
            />
          ))}
          {!episodesToDisplay.length && (
            <div class="text-pretty p-4 text-center">
              No {listenLaterFilter.value === 'unplayed' ? 'unplayed' : 'played'}{' '}
              episodes found.
            </div>
          )}
        </>
      ) : (
        <div class="text-pretty p-4 text-center">
          To save episodes to listen later, just click the{' '}
          <button>
            <Icon sprite="play-later" class="-mb-1 h-5 w-5 text-gray-400" />
          </button>{' '}
          button.
        </div>
      )}
    </div>
  );
}
